import type { WalkQuote, WalkRequest } from '@/libs/walking';
import type { Network } from './network';
import {
  MAX_CANDIDATES,
  MIN_JOURNEYS,
  applyWalks,
  rankJourneys,
  searchCandidates,
  walksToVerify,
  withoutDominated,
  type Journey,
  type JourneyQuery,
  type JourneyResults,
} from './journeys';

export interface VerificationOptions {
  /** Measures walks along the streets within a budget: `fetchWalks`, a stub in the specs. */
  measure: (requests: WalkRequest[], budgetMs: number) => Promise<Map<string, WalkQuote>>;
  budgetMs: number;
  /** A newer search took over: this one must not land. */
  isStale?: () => boolean;
}

/**
 * The journeys to show: RAPTOR on estimated walks finds a pool of candidates, their
 * walks are measured along the streets within the budget, and only the journeys that
 * stand the real walks are ranked. Too few left: one more search past the pool, measured
 * with what remains of the budget. Undefined when a newer search took over meanwhile.
 */
export async function searchVerifiedJourneys(
  network: Network,
  query: JourneyQuery,
  { measure, budgetMs, isStale = () => false }: VerificationOptions,
): Promise<JourneyResults | undefined> {
  const startedAt = Date.now();
  const first = searchCandidates(network, query, MAX_CANDIDATES);
  if (first.originUnreachable || first.destinationUnreachable) {
    const { originUnreachable, destinationUnreachable } = first;
    return { journeys: [], originUnreachable, destinationUnreachable };
  }

  const verify = async (candidates: Journey[], budget: number) => {
    const quotes = await measure(walksToVerify(candidates), budget);
    return candidates
      .map(candidate => applyWalks(candidate, quotes, query.mode, query.time))
      .filter((journey): journey is Journey => journey !== undefined);
  };

  let kept = await verify(first.journeys, budgetMs);
  if (isStale()) return undefined;
  if (kept.length < MIN_JOURNEYS && first.journeys.length > 0) {
    const known = new Set(first.journeys.map(journey => journey.id));
    const more = searchCandidates(
      network,
      { ...query, time: first.nextTime },
      MAX_CANDIDATES,
    ).journeys.filter(journey => !known.has(journey.id));
    kept = [...kept, ...(await verify(more, Math.max(0, budgetMs - (Date.now() - startedAt))))];
    if (isStale()) return undefined;
  }
  return rankJourneys(withoutDominated(kept, query.mode), query.mode);
}
